import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { RotateCcw, SearchIcon } from 'lucide-react';
import { DatePickerInput } from '@/components/common/DatePickerInput';
import { OptionSelect } from '@/components/common/OptionSelect';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { pcmpReportFilterOptionsQueryOptions } from '../../api/pcmpReports.api';
import {
  pcmpPlaceholderName,
  type PcmpMoReportFilterController,
  type PcmpPickedUser,
} from '../../hooks/usePcmpMoReportFilters';
import type { PcmpMoScope } from '../../types/pcmpMoReport.types';
import type { PcmpDivision } from '../../types/pcmpQueue.types';
import { PcmpCodeSelect } from '../PcmpQueue/PcmpCodeSelect';
import { PcmpProjectMultiSelect } from './PcmpProjectMultiSelect';
import { PcmpUserPicker } from './PcmpUserPicker';

// PcmpCodeSelect lists its placeholder first and picking it clears the value:
// that is "One MO" (scope MO is the default), so only the other scope is an option.
const SCOPE_OPTIONS = [{ code: 'ALL', label: 'Many MOs' }] as const;

interface PcmpMoReportFilterBarProps {
  controller: PcmpMoReportFilterController;
  /** The names of picks that came from the URL, once the report has them. */
  pickNames: { mo: string | undefined; reporting: string | undefined };
}

/** Scope MO picks one MO; scope ALL narrows by manager, division and region. */
export function PcmpMoReportFilterBar({ controller, pickNames }: PcmpMoReportFilterBarProps) {
  const { filters, activeFilterCount, isDateRangeInvalid, setDraft, handleApply, handleReset } =
    controller;
  const idPrefix = 'pcmp-mo';

  const { data: options, isLoading: optionsLoading } = useQuery(
    pcmpReportFilterOptionsQueryOptions(),
  );

  const regions = useMemo(
    () =>
      options?.departments
        .filter((department) => !filters.division || department.division === filters.division)
        .map(({ id, name }) => ({ id, name })),
    [options, filters.division],
  );

  const handleDivisionChange = (division: PcmpDivision | undefined) => {
    setDraft('division', division);
    const region = options?.departments.find(
      (department) => department.id === filters.departmentId,
    );
    if (division && region && region.division !== division) setDraft('departmentId', undefined);
  };

  // A pick that came from the URL has no name yet: use the report's.
  const named = (pick: PcmpPickedUser | null, name: string | undefined) =>
    pick && name && pick.name === pcmpPlaceholderName(pick.id) ? { ...pick, name } : pick;
  const mo = named(filters.mo, pickNames.mo);
  const reporting = named(filters.reporting, pickNames.reporting);

  return (
    <form
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        handleApply();
      }}
      className="flex flex-col gap-3 rounded-xl border bg-card p-4 shadow-sm"
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 2xl:grid-cols-6">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${idPrefix}-scope`}>Report for</Label>
          <PcmpCodeSelect<PcmpMoScope>
            id={`${idPrefix}-scope`}
            options={SCOPE_OPTIONS}
            value={filters.scope === 'MO' ? undefined : filters.scope}
            onChange={(value) => setDraft('scope', value ?? 'MO')}
            placeholder="One MO"
          />
        </div>
        {filters.scope === 'MO' ? (
          <div className="flex flex-col gap-1.5 sm:col-span-2">
            <Label htmlFor={`${idPrefix}-user`}>MO</Label>
            <PcmpUserPicker
              kind="mos"
              id={`${idPrefix}-user`}
              value={mo}
              onChange={(value) => setDraft('mo', value)}
              placeholder="Select an MO"
            />
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${idPrefix}-reporting`}>Reporting manager</Label>
              <PcmpUserPicker
                kind="reportingUsers"
                id={`${idPrefix}-reporting`}
                value={reporting}
                onChange={(value) => setDraft('reporting', value)}
                placeholder="Anyone"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${idPrefix}-division`}>Division</Label>
              <PcmpCodeSelect<PcmpDivision>
                id={`${idPrefix}-division`}
                options={options?.divisions}
                value={filters.division}
                onChange={handleDivisionChange}
                disabled={optionsLoading}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${idPrefix}-region`}>Region</Label>
              <OptionSelect
                id={`${idPrefix}-region`}
                options={regions}
                isLoading={optionsLoading}
                value={filters.departmentId}
                onChange={(value) => setDraft('departmentId', value)}
                placeholder="All"
                className="h-10"
                clearable
              />
            </div>
          </>
        )}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${idPrefix}-projects`}>Projects</Label>
          <PcmpProjectMultiSelect
            id={`${idPrefix}-projects`}
            value={filters.projectIds}
            onChange={(value) => setDraft('projectIds', value)}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${idPrefix}-from`}>Proposed From</Label>
          <DatePickerInput
            id={`${idPrefix}-from`}
            value={filters.dateFrom}
            onChange={(value) => setDraft('dateFrom', value)}
            aria-invalid={isDateRangeInvalid}
            className="sm:h-10"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${idPrefix}-to`}>Proposed To</Label>
          <DatePickerInput
            id={`${idPrefix}-to`}
            value={filters.dateTo}
            onChange={(value) => setDraft('dateTo', value)}
            aria-invalid={isDateRangeInvalid}
            className="sm:h-10"
          />
        </div>
      </div>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-xs text-destructive" role="alert">
          {isDateRangeInvalid ? 'Proposed To must be on or after Proposed From.' : null}
        </div>
        <div className="flex items-center justify-end gap-2">
          <Button type="submit" disabled={isDateRangeInvalid} className="h-10 gap-1.5 shadow-sm">
            <SearchIcon className="size-4" />
            Apply
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={handleReset}
            disabled={activeFilterCount === 0 && filters.scope === 'MO'}
            className="h-10 gap-1.5 shadow-sm"
          >
            <RotateCcw className="size-4" />
            Reset{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
          </Button>
        </div>
      </div>
    </form>
  );
}
